/**
 * textPainter — render recorded `text-run` primitives into a PDF page (PDF-2).
 *
 * Two paths, decided per run by {@link resolveFont}:
 *
 *   - **TTF** (searchable): embed the font once via `embedFont(bytes,
 *     {subset:true})`, falling back to `{subset:false}` on the Cyrillic-subset
 *     "Index out of range" throw. Each run is drawn with a raw text matrix
 *     (`BT … Tm … Tj … ET`) that bakes the rotation, height (→ font size),
 *     widthFactor (horizontal scale) and oblique (skew), positioned at the
 *     alignment-adjusted anchor.
 *
 *   - **SHX** (centerline strokes, NOT fill): parse the font via
 *     `parseShxFont`, then for each glyph build its SVG path with
 *     `glyphToSvgPath(glyph, obliqueRad)` and stroke it through a per-glyph
 *     affine (height/fontUnits scale + rotation + widthFactor + pen advance).
 *     Searchable text is lost for SHX (expected — it is geometry).
 *
 * The text matrix contract mirrors AutoCAD: the anchor is the world insertion /
 * alignment point (transformed world→pt by the page transform), `size` =
 * height × MM_TO_PT, rotation in CCW degrees, horizontal scale = widthFactor,
 * skew = tan(obliqueAngle). Alignment (halign/valign) shifts the anchor in
 * text-local space (computed from font metrics) before the rotation is applied.
 *
 * NOTE: AutoCAD TEXT `height` is the CAP height; pdf-lib `size` is the em size.
 * Per the PDF-2 spec we map `size = height × MM_TO_PT` directly (the documented
 * contract); a cap-height correction is a PDF-2b refinement, not a PDF-2 gap.
 */

import {
  beginText,
  endText,
  showText,
  setFontAndSize,
  setTextMatrix,
  pushGraphicsState,
  popGraphicsState,
  setFillingRgbColor,
  setStrokingRgbColor,
  setLineWidth,
  moveTo,
  lineTo,
  appendBezierCurve,
  closePath,
  stroke as strokeOp,
  PDFName,
  PDFOperator,
  PDFOperatorNames,
  type PDFFont,
  type PDFPage,
} from "pdf-lib";
import { parseShxFont, glyphToSvgPath, type ShxFont } from "@dxf-ai-editor/shx-parser";

import type { LayoutPlotSettings, TextStyle } from "@dxf-ai-editor/drawing-model";

import { MM_TO_PT, HAIRLINE_PT, lineweightToPt, type PdfTransform } from "./pdfTransform";
import type { TextRunPrimitive } from "./cadPrimitive";
import { ensureExtGState } from "./extGState";
import {
  resolveFont,
  resolveFontNameAndKind,
  type ResolveFontOptions,
} from "./fontResolve";

const DEG_TO_RAD = Math.PI / 180;

/** A font embedded into the document, cached by logical name. */
interface EmbeddedTtf {
  font: PDFFont;
  /** Whether subset embedding succeeded (false = full embed fallback). */
  subset: boolean;
}

/**
 * The minimal `PDFDocument` surface the painter needs. Declared structurally so
 * the painter does not import the heavy class type and stays test-friendly.
 */
export interface FontEmbedder {
  registerFontkit(fontkit: unknown): void;
  embedFont(
    bytes: Uint8Array,
    options?: { subset?: boolean },
  ): Promise<PDFFont>;
}

/** Result accounting for a text-paint pass. */
export interface TextPaintResult {
  /** Number of text runs rendered (TTF show-text + SHX glyph groups). */
  renderedRuns: number;
  /** Number of runs drawn via the TTF (searchable) path. */
  ttfRuns: number;
  /** Number of runs drawn via the SHX (stroke) path. */
  shxRuns: number;
  /** Number of distinct fonts embedded (TTF). */
  embeddedFonts: number;
  /** Number of SHX stroke path operators emitted (glyph strokes). */
  shxPathOps: number;
  /** Runs whose SHX was substituted by the bundled TTF (fidelity note). */
  substitutedRuns: number;
  /** Runs that fell back to a bundled font (no registry match). */
  fallbackRuns: number;
  /** TTF embeds that needed the subset:false retry (Cyrillic gotcha). */
  subsetRetries: number;
}

/** Options for {@link paintTextRuns}. */
export interface PaintTextRunsOptions {
  /** Text-style table: name → TextStyle (for font resolution). */
  textStyles: ReadonlyMap<string, TextStyle>;
  /** Font-resolution overrides (injected bytes / bundled loader for tests). */
  fontResolve?: ResolveFontOptions;
  /** Fontkit instance to register before TTF embedding. */
  fontkit: unknown;
  /**
   * Multiplier from `run.height` (the run's authored height unit) to pt. For
   * PAPER-space text the height is already in paper mm so the default
   * {@link MM_TO_PT} is exact. For VIEWPORT MODEL text the height is in MODEL
   * units; the caller passes `unitToPaperScale × MM_TO_PT` so the glyph SIZE is
   * scaled by the VP's model→paper scale (the composed `transform` only scales
   * the anchor POSITION, not the scalar font size). Default: {@link MM_TO_PT}.
   */
  heightToPt?: number;
  /**
   * Additional rotation (CCW degrees) applied to EVERY run on top of its own
   * `run.rotation`. For VP model text this is `−twistDeg`: `modelToPaper`
   * rotates the model view by −twist, so model-axis-aligned text appears
   * rotated by −twist on paper. Default: `0` (paper-space text unchanged).
   */
  extraRotationDeg?: number;
  /**
   * PDF-6.4 — AutoCAD "Lines merge / Overwrite" PC3 plot setting, threaded from
   * the sheet render context (the SAME value forwarded into `emitBatchedPaths` /
   * `emitHatchPattern`). When `"merge"`, EVERY painted text run is wrapped in an
   * ExtGState carrying `/BM /Multiply` so overlapping glyphs darken like other
   * geometry — matching the `cadPrimitive.ts` contract that merge mode wraps
   * every painted primitive. Default / absent → `"overwrite"` (no `/BM`, PDF
   * Normal blend, byte-identical to pre-6.4 text output).
   */
  mergeControl?: "overwrite" | "merge";
}

/**
 * Paint all `text-run` primitives onto `page`, embedding TTFs (with the
 * subset→subset:false retry) and stroking SHX glyphs. Returns paint accounting.
 *
 * `embedder` is the PDFDocument (structurally typed). `transform` maps RAW
 * world mm → page pt (the same closure the geometry path uses, so text and
 * geometry share one coordinate frame).
 */
export async function paintTextRuns(
  page: PDFPage,
  embedder: FontEmbedder,
  runs: readonly TextRunPrimitive[],
  transform: PdfTransform,
  settings: LayoutPlotSettings,
  options: PaintTextRunsOptions,
): Promise<TextPaintResult> {
  const result: TextPaintResult = {
    renderedRuns: 0,
    ttfRuns: 0,
    shxRuns: 0,
    embeddedFonts: 0,
    shxPathOps: 0,
    substitutedRuns: 0,
    fallbackRuns: 0,
    subsetRetries: 0,
  };
  if (runs.length === 0) return result;

  // Per-run height→pt multiplier (paper-space default = MM_TO_PT; VP model text
  // passes unitToPaperScale × MM_TO_PT) + extra rotation (VP twist) folded in.
  const heightToPt = options.heightToPt ?? MM_TO_PT;
  const extraRotationDeg = options.extraRotationDeg ?? 0;
  // PDF-6.4: Lines merge / Overwrite. "merge" wraps each painted text run in
  // `/BM /Multiply` (alongside any per-run alpha); "overwrite" (default) emits
  // no blend mode — byte-identical to pre-6.4 text output.
  const mergeControl: "overwrite" | "merge" =
    options.mergeControl ?? "overwrite";

  // Register fontkit once (idempotent — pdf-lib stores the last instance).
  embedder.registerFontkit(options.fontkit);

  // Per-font caches keyed by logical font name.
  const ttfCache = new Map<string, EmbeddedTtf>();
  const shxCache = new Map<string, ShxFont | null>();

  for (const run of runs) {
    if (run.value === "") continue; // empty TEXT slot — nothing visible
    const style = options.textStyles.get(run.styleName);
    const resolved = await resolveFont(style, options.fontResolve);
    if (resolved.fallback) result.fallbackRuns += 1;
    if (resolved.substituted) result.substitutedRuns += 1;

    if (resolved.kind === "ttf") {
      let cached = ttfCache.get(resolved.name);
      if (!cached) {
        cached = await embedTtf(embedder, resolved.bytes);
        if (!cached.subset) result.subsetRetries += 1;
        ttfCache.set(resolved.name, cached);
        result.embeddedFonts += 1;
      }
      paintTtfRun(
        page,
        run,
        cached.font,
        transform,
        heightToPt,
        extraRotationDeg,
        mergeControl,
      );
      result.ttfRuns += 1;
      result.renderedRuns += 1;
    } else {
      // SHX stroke path.
      let font = shxCache.get(resolved.name);
      if (font === undefined) {
        const parsed = parseShxFont(resolved.bytes);
        font = parsed.ok ? parsed.font : null;
        shxCache.set(resolved.name, font);
      }
      if (font) {
        const ops = paintShxRun(
          page,
          run,
          font,
          transform,
          settings,
          heightToPt,
          extraRotationDeg,
          mergeControl,
        );
        result.shxPathOps += ops;
        result.shxRuns += 1;
        result.renderedRuns += 1;
      }
    }
  }

  return result;
}

/* ── TTF embedding (subset → subset:false retry) ────────────────────── */

/**
 * Embed a TTF, trying `{subset:true}` first and retrying `{subset:false}` on
 * the Cyrillic-subset "Index out of range" throw (the documented gotcha,
 * PLAN-plot-pdf.md Appendix D). Returns the font + whether subsetting held.
 */
async function embedTtf(
  embedder: FontEmbedder,
  bytes: Uint8Array,
): Promise<EmbeddedTtf> {
  try {
    const font = await embedder.embedFont(bytes, { subset: true });
    return { font, subset: true };
  } catch {
    // Subset embedding can throw on some Cyrillic-bearing fonts ("Index out of
    // range") — fall back to a full embed, which always works.
    const font = await embedder.embedFont(bytes, { subset: false });
    return { font, subset: false };
  }
}

/* ── TTF run painting ───────────────────────────────────────────────── */

/**
 * PDF-6.4 — resolve the ExtGState `gs` operator a painted text run needs (if
 * any) for per-entity transparency (`run.alpha ∈ [0, 1)`) and/or Lines-merge
 * blend (`mergeControl === "merge"`). Returns the operator to push immediately
 * after `pushGraphicsState()`, or `null` when the run is opaque AND not in merge
 * mode (no `gs` needed).
 *
 * The alpha guard MIRRORS the path emitter (`batchPaint.ts` flushRun): alpha ∈
 * [0, 1) — a fully-transparent run (alpha === 0, DXF 440 `0x02000000`) STILL
 * emits its `/CA 0 /ca 0` dict so the glyphs plot invisible (AutoCAD parity for
 * 100% transparency), while an opaque run (alpha ≥ 1 / undefined) emits no
 * alpha. `ensureExtGState` sets BOTH `/CA` (stroke, used by SHX strokes) and
 * `/ca` (fill, used by TTF glyph fills) to the same value, so one call covers
 * both glyph paint kinds.
 */
function textRunGsOp(
  page: PDFPage,
  run: TextRunPrimitive,
  mergeControl: "overwrite" | "merge",
): PDFOperator | null {
  const needsAlpha =
    run.alpha !== undefined && run.alpha >= 0 && run.alpha < 1;
  const needsBlend = mergeControl === "merge";
  if (!needsAlpha && !needsBlend) return null;
  const gsName = ensureExtGState(page, {
    alpha: needsAlpha ? run.alpha : undefined,
    blendMode: needsBlend ? "Multiply" : undefined,
  });
  return PDFOperator.of(PDFOperatorNames.SetGraphicsStateParams, [
    PDFName.of(gsName),
  ]);
}

/**
 * Draw one TTF run with a raw text matrix. The matrix bakes:
 *   - font size = run.height × heightToPt (paper-mm → MM_TO_PT; VP model text →
 *     unitToPaperScale × MM_TO_PT so the glyph SIZE follows the VP scale),
 *   - rotation θ = (run.rotation + extraRotationDeg) (CCW degrees),
 *   - horizontal scale = widthFactor,
 *   - skew = tan(obliqueAngle).
 * The anchor is the alignment-adjusted insertion point, mapped world→pt.
 */
function paintTtfRun(
  page: PDFPage,
  run: TextRunPrimitive,
  font: PDFFont,
  transform: PdfTransform,
  heightToPt: number,
  extraRotationDeg: number,
  mergeControl: "overwrite" | "merge",
): void {
  const sizePt = runSizePt(run.height, heightToPt);
  if (sizePt <= 0) return;

  const widthPt = font.widthOfTextAtSize(run.value, sizePt);
  const heightPt = font.heightAtSize(sizePt);
  const { dx, dy } = alignmentOffset(
    run.hJustification,
    run.vJustification,
    widthPt,
    heightPt,
    sizePt,
  );

  // Anchor in page pt (world→pt). The page transform may itself carry a plot
  // scale/rotation, but text is drawn at the run's own size + rotation in the
  // post-transform frame, so we anchor at the mapped point and apply the run's
  // local text matrix from there.
  const anchor = transform(run.position.x, run.position.y);

  const theta = (run.rotation + extraRotationDeg) * DEG_TO_RAD;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const skew = Math.tan(run.obliqueAngle * DEG_TO_RAD);
  const wf = run.widthFactor || 1;

  // GitHub Codex review PR #558 round 2 (round-4 local read-only follow-up)
  // P2 — `dy` is a LOCAL-Y shift measured against the UNMIRRORED glyph
  // box's own frame (e.g. vJust=Middle: box spans local y∈[0,heightPt],
  // `dy=-heightPt/2` shifts the origin DOWN so the box's CENTER lands on
  // the target anchor). The glyph basis below (`mirroredTextMatrixBasis`)
  // already flips the local-Y axis for a mirrored run — its glyph box
  // reflects to y∈[-heightPt,0] around the SAME origin — so the SAME
  // local-Y offset must flip sign too, else the alignment shift displaces
  // the anchor instead of keeping it at the mirrored box's OWN justified
  // point.
  //
  // GitHub Codex review PR #558 round 2 (round-9 GitHub bot follow-up) P2 —
  // the SAME reasoning applies to `dx` (horizontal) for a `backward`
  // (local-X flip) run — a block-ATTRIB's INDEPENDENT bit-2 handedness
  // (Dimension/Multileader never carry it, so `run.backward` stays
  // undefined/false there, making this a no-op — see
  // {@link mirroredTextMatrixBasis}'s own doc for the two-axis rationale).
  const mirrorSign = run.mirrored ? -1 : 1;
  const backwardSign = run.backward ? -1 : 1;
  const effectiveDx = dx * backwardSign;
  const effectiveDy = dy * mirrorSign;

  // Local text-space alignment shift (dx,dy in pt at the run's orientation),
  // rotated into the page frame.
  const ex = anchor[0] + (effectiveDx * cos - effectiveDy * sin);
  const ey = anchor[1] + (effectiveDx * sin + effectiveDy * cos);

  // Text matrix Tm = [a b c d e f]. GitHub Codex review PR #558 round 2 —
  // MIRRTEXT handedness flips the LOCAL Y-BASIS, `backward` flips the LOCAL
  // X-BASIS (round-9), and the oblique skew term stays paired with the
  // ORIGINAL (non-mirrored, non-backward) sign — see
  // {@link mirroredTextMatrixBasis}'s own doc for the full composition
  // proof + the round-2/round-3/round-9 local/GitHub-review history.
  const { a, b, c, d } = mirroredTextMatrixBasis(
    theta,
    wf,
    skew,
    run.mirrored ?? false,
    run.backward ?? false,
  );

  const glyphs = font.encodeText(run.value);

  // PDF-6.4: per-entity transparency (`/ca` fill alpha) and/or Lines-merge
  // `/BM /Multiply`. The gs op is pushed immediately after the graphics-state
  // save so the alpha/blend applies ONLY to this run's glyphs.
  const gsOp = textRunGsOp(page, run, mergeControl);

  const ops: PDFOperator[] = [pushGraphicsState()];
  if (gsOp) ops.push(gsOp);
  ops.push(
    setFillingRgbColor(run.color.r, run.color.g, run.color.b),
    beginText(),
    setFontAndSize(fontRefName(page, font), sizePt),
    setTextMatrix(a, b, c, d, ex, ey),
    showText(glyphs),
    endText(),
    popGraphicsState(),
  );
  page.pushOperators(...ops);
}

/**
 * Resolve the page-resource font name (`F1-0` …) for an embedded font.
 *
 * `setFontAndSize` needs the resource KEY pdf-lib assigned the font in the
 * page's `/Resources /Font` dict. We use the public `page.setFont(font)` side
 * effect (it registers the font on the page and records the key on
 * `page.fontKey` as a `PDFName`), then read that key. This mirrors exactly what
 * pdf-lib's own `drawText` does internally via `setOrEmbedFont`.
 */
function fontRefName(page: PDFPage, font: PDFFont): string {
  page.setFont(font);
  const fontKey = (page as unknown as { fontKey?: { toString(): string } })
    .fontKey;
  // PDFName.toString() is like `/F1-0`; the `Tf` operand omits the slash.
  return (fontKey?.toString() ?? "/F1-0").replace(/^\//, "");
}

/**
 * Compute the font size in pt for a run height, applying the per-run
 * `heightToPt` multiplier. PAPER-space text uses MM_TO_PT (height authored in
 * paper mm at 1:1); VIEWPORT MODEL text uses `unitToPaperScale × MM_TO_PT` so
 * the glyph size follows the VP's model→paper scale. Guards non-finite / ≤0.
 */
export function runSizePt(height: number, heightToPt: number): number {
  const pt = height * heightToPt;
  return Number.isFinite(pt) && pt > 0 ? pt : 0;
}

/**
 * The `[a b c d]` (x-basis, y-basis) portion of a PDF text matrix Tm for a
 * run at rotation `theta` (radians), width factor `wf`, oblique skew `skew`
 * (`tan(obliqueAngle)`), MIRRTEXT Y-handedness (`mirrored`), and — GitHub
 * Codex review PR #558 round 2 (round-9 GitHub bot follow-up) P2 — the
 * INDEPENDENT local-X handedness (`backward`).
 *
 * Exported standalone for direct unit coverage (GitHub Codex review PR #558
 * round 2 — round-3 local read-only follow-up P2 established the Y-only
 * `mirrored` axis; round-9 generalises it to also carry `backward`). Both
 * flips mirror `sceneTextMeshFactories.ts::applyMirrorScale`'s two
 * INDEPENDENT decoded bits: `mirrored` = local-Y flip (Dimension/
 * Multileader's OWN `mirrored` field, and the "upsideDown"/bit-4 half of a
 * block-ATTRIB's `generationFlags`, both `mesh.scale.set(1,-1,1)`-shaped);
 * `backward` = local-X flip (a block-ATTRIB's "backward"/bit-2 half ONLY —
 * Dimension/Multileader never carry this bit, so their callers always pass
 * `backward=false`, reducing exactly to the round-3 Y-only formula).
 *
 * The oblique SKEW contribution does NOT flip sign alongside EITHER mirror
 * axis: the viewport composes SHEAR first (in local glyph space, independent
 * of handedness), THEN the local-axis scale on top of the already-sheared
 * shape — composition proof: `M = ROTATE(θ) ∘ SCALE(backwardSign,
 * mirrorSign) ∘ SKEW ∘ SCALE(wf,1)`. Basis e1=(1,0): SCALE gives (wf,0) →
 * SKEW is a no-op on it (skew·0=0) → the axis-scale gives
 * (backwardSign·wf, 0) → ROTATE gives `(backwardSign·wf·cos,
 * backwardSign·wf·sin)`, exactly `a`/`b`. Basis e2=(0,1): SCALE gives (0,1)
 * → SKEW gives (skew,1) → the axis-scale gives (backwardSign·skew,
 * mirrorSign) → ROTATE gives `(backwardSign·skew·cos − mirrorSign·sin,
 * backwardSign·skew·sin + mirrorSign·cos)`, exactly `c`/`d` below
 * (`backwardSign=mirrorSign=1` reduces to the plain unmirrored formula;
 * `backwardSign=1` alone reduces to the round-3 Y-only formula).
 */
export function mirroredTextMatrixBasis(
  theta: number,
  wf: number,
  skew: number,
  mirrored: boolean,
  backward = false,
): { a: number; b: number; c: number; d: number } {
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const mirrorSign = mirrored ? -1 : 1;
  const backwardSign = backward ? -1 : 1;
  return {
    a: backwardSign * wf * cos,
    b: backwardSign * wf * sin,
    c: -mirrorSign * sin + backwardSign * skew * cos,
    d: mirrorSign * cos + backwardSign * skew * sin,
  };
}

/**
 * Anchor offset (in text-local pt, +x right / +y up) for a (halign, valign)
 * pair so the run reads correctly. AutoCAD codes:
 *   halign (72): 0 Left · 1 Center · 2 Right · 3 Aligned · 4 Middle · 5 Fit
 *   valign (74): 0 Baseline · 1 Bottom · 2 Middle · 3 Top
 * The recorded anchor is the alignment point for justified text, so we shift
 * by minus the corresponding fraction of the text extent.
 */
function alignmentOffset(
  hJust: number,
  vJust: number,
  widthPt: number,
  heightPt: number,
  sizePt: number,
): { dx: number; dy: number } {
  let dx = 0;
  if (hJust === 1 || hJust === 4) dx = -widthPt / 2; // Center / Middle
  else if (hJust === 2) dx = -widthPt; // Right
  // Left / Aligned / Fit anchor at the left baseline (dx stays 0).

  let dy = 0;
  // Vertical anchor. pdf-lib `heightAtSize` is the ascender+descender extent;
  // we treat the baseline as the reference (dy 0) and shift for other anchors.
  if (vJust === 1) dy = 0; // Bottom ≈ baseline for single-line (descender small)
  else if (vJust === 2) dy = -heightPt / 2; // Middle of the glyph box
  else if (vJust === 3) dy = -heightPt; // Top
  // AutoCAD "Middle" (halign 4) centres vertically about the cap box too, even
  // though DXF records vJust 0 (baseline) for it; shift by half the cap height.
  else if (hJust === 4) dy = -sizePt / 2;
  return { dx, dy };
}

/* ── SHX run painting (centerline strokes) ──────────────────────────── */

/**
 * Draw one SHX run by stroking each glyph's centerline path. Returns the number
 * of stroke operator-groups emitted (one per non-empty glyph). SHX glyphs are
 * in font units with +Y up and origin at the glyph baseline-left; we scale by
 * `size / capHeight`, advance the pen by `advanceX × scale`, rotate by the run
 * rotation and shear by the oblique angle, then stroke (hairline / DIMTXT-ish).
 */
function paintShxRun(
  page: PDFPage,
  run: TextRunPrimitive,
  font: ShxFont,
  transform: PdfTransform,
  settings: LayoutPlotSettings,
  heightToPt: number,
  extraRotationDeg: number,
  mergeControl: "overwrite" | "merge",
): number {
  const sizePt = runSizePt(run.height, heightToPt);
  if (sizePt <= 0) return 0;

  // PDF-6.4: resolve the ExtGState `gs` op (per-entity transparency `/CA` for
  // the strokes, and/or Lines-merge `/BM /Multiply`) ONCE for the whole run; it
  // is pushed inside each glyph's own `q … Q` save-state so the alpha/blend
  // applies per glyph stroke. Opaque + overwrite → null (no gs op, unchanged).
  const gsOp = textRunGsOp(page, run, mergeControl);

  // Font-unit cap height: SHX `above` is the baseline→cap distance in font
  // units. Scale so `above` maps to the run's size (height) in pt.
  const capUnits = font.above > 0 ? font.above : 1;
  const unitScale = sizePt / capUnits;
  const obliqueRad = run.obliqueAngle * DEG_TO_RAD;
  const wf = run.widthFactor || 1;

  // Total advance for alignment: sum of glyph advances × scale × widthFactor.
  let totalAdvanceUnits = 0;
  for (const ch of run.value) {
    const g = font.glyphs.get(ch.codePointAt(0) ?? 0);
    if (g) totalAdvanceUnits += g.advanceX;
  }
  const totalWidthPt = totalAdvanceUnits * unitScale * wf;
  const capHeightPt = sizePt;
  const { dx: alignDx, dy: alignDy } = alignmentOffset(
    run.hJustification,
    run.vJustification,
    totalWidthPt,
    capHeightPt,
    sizePt,
  );

  const theta = (run.rotation + extraRotationDeg) * DEG_TO_RAD;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const anchor = transform(run.position.x, run.position.y);

  // GitHub Codex review PR #558 round 2 P2 (local read-only follow-up,
  // P1) — same MIRRTEXT handedness treatment as `paintTtfRun` (flip the
  // LOCAL Y coefficient — `sceneTextMeshFactories.ts::applyMirrorScale`'s
  // `mesh.scale.set(1, -1, 1)`), applied to the SHX fallback path so a
  // Dimension/Multileader resolved to an SHX-backed text style ALSO
  // reflects in the PDF the SAME way as the viewport — full parity across
  // both text-rendering backends (ПРИОРИТЕТ #1).
  //
  // GitHub Codex review PR #558 round 2 (round-9 GitHub bot follow-up) P2
  // — a block-ATTRIB's INDEPENDENT bit-2 `backward` (local-X flip) needs
  // the SAME treatment on the x/pen-advance terms this comment previously
  // said to "leave untouched" — that was correct ONLY for Dimension/
  // Multileader (which never carry a `backward`-equivalent bit, so
  // `run.backward` stays undefined/false there, making this a no-op).
  const mirrorSign = run.mirrored ? -1 : 1;
  const backwardSign = run.backward ? -1 : 1;
  // GitHub Codex review PR #558 round 2 (round-4 local read-only
  // follow-up) P2 — `alignDy`/`alignDx` are LOCAL shifts measured against
  // the UNMIRRORED glyph box's own frame (`paintTtfRun`'s sibling fix has
  // the full derivation); the SHX path's glyph basis below already flips
  // local-Y/local-X for a mirrored/backward run, so the SAME alignment
  // shifts must flip sign too, else they displace the origin instead of
  // keeping it at the mirrored/backward box's own justified point.
  const effectiveAlignDx = alignDx * backwardSign;
  const effectiveAlignDy = alignDy * mirrorSign;
  // Origin in page pt = anchor + rotated alignment shift.
  const ox = anchor[0] + (effectiveAlignDx * cos - effectiveAlignDy * sin);
  const oy = anchor[1] + (effectiveAlignDx * sin + effectiveAlignDy * cos);

  // Stroke weight: hairline minimum (SHX strokes are thin centerlines).
  const lwPt = Math.max(lineweightToPt(0, settings), HAIRLINE_PT);

  let penUnitsX = 0; // glyph-space pen advance (font units, pre-scale)
  let pathGroups = 0;

  for (const ch of run.value) {
    const cp = ch.codePointAt(0) ?? 0;
    const glyph = font.glyphs.get(cp);
    if (!glyph) continue;
    const d = glyphToSvgPath(glyph, obliqueRad);
    if (d !== "") {
      // Map the glyph-space SVG path through the run's affine into page pt.
      // glyph→run-local: scale by unitScale (× wf horizontally), offset by the
      // pen advance; run-local→page: rotate by θ, translate to origin.
      const ops = strokeSvgPathThroughAffine(
        d,
        run.color,
        lwPt,
        // a,b,c,d,e,f mapping glyph units → page pt (mirrorSign=backwardSign=1
        // for the common unmirrored/non-backward case, reducing to the
        // original formula). `backwardSign` scales the SAME x-basis terms
        // (a,b) the pen-advance position (e,f) projects onto — a backward
        // run's glyphs advance in the OPPOSITE local direction, matching the
        // WebGL mesh's whole-mesh `scale.x=-1` flip (round-9):
        // x' = ox + backwardSign·(penX + x·unitScale·wf)·cos − (mirrorSign·y·unitScale)·sin
        // y' = oy + backwardSign·(penX + x·unitScale·wf)·sin + (mirrorSign·y·unitScale)·cos
        {
          a: backwardSign * unitScale * wf * cos,
          b: backwardSign * unitScale * wf * sin,
          c: mirrorSign * -unitScale * sin,
          d: mirrorSign * unitScale * cos,
          e: ox + backwardSign * penUnitsX * unitScale * wf * cos,
          f: oy + backwardSign * penUnitsX * unitScale * wf * sin,
        },
        gsOp,
      );
      page.pushOperators(...ops);
      pathGroups += 1;
    }
    penUnitsX += glyph.advanceX;
  }
  return pathGroups;
}

/** Affine glyph-units → page-pt (column-major like a PDF cm matrix). */
interface GlyphAffine {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

/**
 * Convert an SVG path `d` string (in glyph units, from `glyphToSvgPath`) into a
 * stroked PDF operator group, mapping every coordinate through `m` (glyph→pt).
 *
 * We parse the path via pdf-lib's `svgPathToOperators` to honour M/L/C/A/Z, but
 * those operators already pre-transform to a fixed frame; instead we re-walk
 * the SVG commands ourselves so the affine maps each control point exactly.
 */
function strokeSvgPathThroughAffine(
  d: string,
  color: { r: number; g: number; b: number },
  lwPt: number,
  m: GlyphAffine,
  gsOp: PDFOperator | null,
): PDFOperator[] {
  // PDF-6.4: the per-run ExtGState `gs` op (alpha `/CA` and/or `/BM /Multiply`)
  // is pushed immediately after `pushGraphicsState()` so it applies to THIS
  // glyph's stroke. `null` → opaque + overwrite → no gs op (unchanged output).
  const ops: PDFOperator[] = [pushGraphicsState()];
  if (gsOp) ops.push(gsOp);
  ops.push(
    setLineWidth(lwPt),
    setStrokingRgbColor(color.r, color.g, color.b),
  );
  const map = (x: number, y: number): [number, number] => [
    m.a * x + m.c * y + m.e,
    m.b * x + m.d * y + m.f,
  ];
  walkSvgPath(d, {
    moveTo: (x, y) => {
      const p = map(x, y);
      ops.push(moveTo(p[0], p[1]));
    },
    lineTo: (x, y) => {
      const p = map(x, y);
      ops.push(lineTo(p[0], p[1]));
    },
    bezierTo: (x1, y1, x2, y2, x, y) => {
      const c1 = map(x1, y1);
      const c2 = map(x2, y2);
      const e = map(x, y);
      ops.push(appendBezierCurve(c1[0], c1[1], c2[0], c2[1], e[0], e[1]));
    },
    close: () => ops.push(closePath()),
  });
  ops.push(strokeOp(), popGraphicsState());
  return ops;
}

/* ── Minimal SVG path walker (M/L/C/A/Z) ────────────────────────────── */

interface SvgPathSink {
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  bezierTo(x1: number, y1: number, x2: number, y2: number, x: number, y: number): void;
  close(): void;
}

/**
 * Walk an absolute SVG path string emitting glyph-unit coordinates to the sink.
 * `glyphToSvgPath` only emits absolute `M`, `L` and `A` (circular arc)
 * commands; we tessellate `A` into cubic beziers so the stroke is exact. All
 * coordinates stay in glyph units (the caller's affine maps them to pt).
 */
function walkSvgPath(d: string, sink: SvgPathSink): void {
  // `glyphToSvgPath` emits only command letters and fixed-point numbers (no
  // exponent notation — its `fmt()` uses toFixed), so a simple number token
  // suffices (and avoids a ReDoS-prone exponent alternation).
  const tokens = d.match(/[MLAZmlaz]|-?\d*\.?\d+/g);
  if (!tokens) return;
  let i = 0;
  let cx = 0;
  let cy = 0;
  const num = (): number => Number(tokens[i++]);
  while (i < tokens.length) {
    const cmd = tokens[i++];
    switch (cmd) {
      case "M": {
        cx = num();
        cy = num();
        sink.moveTo(cx, cy);
        break;
      }
      case "L": {
        cx = num();
        cy = num();
        sink.lineTo(cx, cy);
        break;
      }
      case "A": {
        // rx ry xrot large sweep x y — circular arc (rx == ry for SHX).
        const rx = num();
        num(); // ry (== rx)
        num(); // x-axis-rotation (always 0)
        const large = num();
        const sweepFlag = num();
        const ex = num();
        const ey = num();
        arcToBeziers(cx, cy, rx, large !== 0, sweepFlag !== 0, ex, ey, sink);
        cx = ex;
        cy = ey;
        break;
      }
      case "Z":
      case "z":
        sink.close();
        break;
      default:
        // Unknown / repeated implicit command — skip a number to stay aligned.
        break;
    }
  }
}

/**
 * Tessellate a circular SVG arc (rx == ry == r) from the current point to
 * (ex, ey) into cubic beziers (≤90° spans), emitting `bezierTo` to the sink.
 * Mirrors the endpoint→center arc conversion of the SVG spec for equal radii.
 */
function arcToBeziers(
  sx: number,
  sy: number,
  r: number,
  largeArc: boolean,
  sweep: boolean,
  ex: number,
  ey: number,
  sink: SvgPathSink,
): void {
  if (r <= 0 || (sx === ex && sy === ey)) {
    sink.lineTo(ex, ey);
    return;
  }
  // Midpoint + perpendicular distance to the centre (equal-radius circle).
  const dx = (sx - ex) / 2;
  const dy = (sy - ey) / 2;
  const chord = Math.hypot(sx - ex, sy - ey);
  const half = chord / 2;
  const rr = Math.max(r, half); // clamp degenerate radius
  const h = Math.sqrt(Math.max(0, rr * rr - half * half));
  const mx = (sx + ex) / 2;
  const my = (sy + ey) / 2;
  // Two candidate centres; pick by large-arc/sweep flags (SVG rule).
  const ux = -dy / half;
  const uy = dx / half;
  const sign = largeArc === sweep ? -1 : 1;
  const cxc = mx + sign * h * ux;
  const cyc = my + sign * h * uy;
  const a0 = Math.atan2(sy - cyc, sx - cxc);
  const a1 = Math.atan2(ey - cyc, ex - cxc);
  let delta = a1 - a0;
  if (sweep && delta < 0) delta += Math.PI * 2;
  if (!sweep && delta > 0) delta -= Math.PI * 2;
  const spans = Math.max(1, Math.ceil(Math.abs(delta) / (Math.PI / 2)));
  const step = delta / spans;
  for (let s = 0; s < spans; s++) {
    const t0 = a0 + step * s;
    const t1 = t0 + step;
    const k = (4 / 3) * Math.tan((t1 - t0) / 4);
    const p0x = cxc + rr * Math.cos(t0);
    const p0y = cyc + rr * Math.sin(t0);
    const p3x = cxc + rr * Math.cos(t1);
    const p3y = cyc + rr * Math.sin(t1);
    const c1x = p0x - k * rr * Math.sin(t0);
    const c1y = p0y + k * rr * Math.cos(t0);
    const c2x = p3x + k * rr * Math.sin(t1);
    const c2y = p3y - k * rr * Math.cos(t1);
    sink.bezierTo(c1x, c1y, c2x, c2y, p3x, p3y);
  }
}

/* Re-export for callers that only need name/kind without bytes. */
export { resolveFontNameAndKind };
